<template>
  <base-modal
    name="adminVideoBearbeiten"
    :title="get('video').title || $t('shop.admin_video_edit.title_edit')"
    :subtitle="$t('shop.admin_video_edit.subtitle')"
    :save-text="$t('shop.admin_video_edit.save')"
    :loading="saveLoader"
    :save-disabled="!canSave"
    @submit="saveVideoChange($event, get('video').id)"
  >
    <!-- Alert Banner -->
    <v-alert
      v-if="!get('video').published"
      type="warning"
      text
      dense
      class="mb-4"
      icon="mdi-alert"
    >
      {{ $t('shop.admin_video_edit.not_published_warning') }}
    </v-alert>

    <!-- Tabs -->
    <v-tabs
      v-model="activeTab"
      color="primary"
      class="mb-6 rounded-lg d-flex justify-start"
      height="48"
    >
      <v-tab class="text-none font-weight-bold">{{ $t('shop.admin_video_edit.tab_details') }}</v-tab>
      <v-tab class="text-none font-weight-bold">{{ $t('shop.admin_video_edit.tab_monetization') }}</v-tab>
      <v-tab class="text-none font-weight-bold">
        {{ $t('shop.admin_video_edit.tab_visibility') }}
        <v-badge
          v-if="!get('video').published"
          color="error"
          dot
          inline
          class="ml-1"
        ></v-badge>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <!-- Tab 1: Details -->
      <v-tab-item>
        <v-card flat>
          <v-card-text class="px-0">
            <!-- Titel -->
            <div class="mb-0">
              <div class="text-body-2 font-weight-bold mb-2">{{ $t('shop.admin_video_edit.title_label') }}</div>
              <v-text-field
                v-model="get('video').title"
                name="title"
                :placeholder="$t('shop.admin_video_edit.title_placeholder')"
                :rules="videoTitleRules"
                outlined
                required
                dense
                counter="52"
                hide-details="auto"
              />
            </div>

            <!-- Beschreibung -->
            <div>
              <div class="text-body-2 font-weight-medium mb-2">{{ $t('shop.admin_video_edit.description_label') }}</div>
              <v-textarea
                v-model="get('video').description"
                name="description"
                :placeholder="$t('shop.admin_video_edit.description_placeholder')"
                outlined
                dense
                :rules="videoDescriptionRules"
                counter="2000"
                hide-details="auto"
                rows="4"
              ></v-textarea>
            </div>

            <!-- Kategorie -->
            <div class="mb-6">
              <div class="text-body-2 font-weight-bold mb-2">{{ $t('shop.admin_video_edit.categories_label') }}</div>
              <v-select
                multiple
                v-model="selectedCategories"
                :items="get('categories')"
                item-text="name"
                item-value="id"
                :placeholder="$t('shop.admin_video_edit.categories_placeholder')"
                :rules="[(v) => (v && v.length > 0) || $t('shop.admin_video_edit.categories_required')]"
                outlined
                dense
                chips
                deletable-chips
                hide-details="auto"
              >
                <template v-slot:selection="{ item }">
                  <v-chip
                    color="primary"
                    small
                    close
                    @click:close="deleteVideoFromCategory(item)"
                  >
                    {{ item.name }}
                  </v-chip>
                </template>
              </v-select>
              <div class="text-caption grey--text mt-1">{{ $t('shop.admin_video_edit.categories_hint') }}</div>
            </div>

            <!-- Altersfreigabe -->
            <div class="mb-6">
              <div class="text-body-2 font-weight-bold mb-2">{{ $t('shop.admin_video_edit.age_rating_label') }}</div>
              <v-select
                name="fsk"
                v-model="get('video').fsk"
                :items="fskItems"
                item-text="fsk"
                item-value="id"
                :placeholder="$t('shop.admin_video_edit.age_rating_placeholder')"
                outlined
                dense
                hide-details="auto"
              ></v-select>
            </div>

            <v-divider class="my-6"></v-divider>

            <!-- Thumbnail -->
            <div class="mb-2">
              <div class="text-body-2 font-weight-bold mb-2">{{ $t('shop.admin_video_edit.thumbnail_label') }}</div>

              <v-row class="ma-0">
                <!-- Upload Bereich -->
                <v-col
                  cols="12"
                  md="6"
                  class="pa-0 pr-md-2"
                >
                  <v-card
                    dark
                    class="upload-section"
                    elevation="0"
                  >
                    <div
                      class="drop-zone"
                      :class="{ 'drop-zone--active': isDraggingImage }"
                      @drop.prevent="onDropImage"
                      @dragover.prevent="isDraggingImage = true"
                      @dragleave.prevent="isDraggingImage = false"
                      @click="triggerImageInput"
                    >
                      <div class="drop-zone-content">
                        <v-icon
                          color="#606060"
                          size="64"
                          class="mb-4"
                        >
                          mdi-image-outline
                        </v-icon>
                        <div class="drop-zone-text mb-2">
                          {{ $t('shop.admin_video_edit.thumbnail_dropzone') }}
                        </div>
                        <div class="drop-zone-formats">
                          {{ $t('shop.admin_video_edit.thumbnail_formats') }}
                        </div>
                      </div>

                      <!-- Hidden VueFileAgent -->
                      <VueFileAgent
                        ref="image"
                        :deletable="true"
                        :meta="true"
                        :accept="'image/*'"
                        :maxSize="'30MB'"
                        :maxFiles="1"
                        :errorText="{
                          type: $t('shop.admin_video_edit.thumbnail_error_type'),
                          size: $t('shop.admin_video_edit.thumbnail_error_size'),
                        }"
                        @beforedelete="onBeforeDeleteImage($event)"
                        @delete="imageDeleted($event)"
                        @change="thumbnailUpload(get('video').id)"
                        v-model="image"
                        style="display: none"
                      ></VueFileAgent>
                    </div>
                  </v-card>
                </v-col>

                <!-- Aktuelles Thumbnail -->
                <v-col
                  cols="12"
                  md="6"
                  class="pa-0 pl-md-2"
                >
                  <v-card
                    dark
                    class="thumbnail-preview"
                    elevation="0"
                  >
                    <div
                      v-if="get('video').image"
                      class="thumbnail-preview-wrapper"
                    >
                      <v-img
                        :src="imageSrc(get('video').image)"
                        height="200"
                        width="100%"
                        class="rounded"
                        contain
                      >
                        <template v-slot:placeholder>
                          <v-row
                            class="fill-height ma-0"
                            align="center"
                            justify="center"
                          >
                            <v-progress-circular
                              indeterminate
                              color="primary"
                            ></v-progress-circular>
                          </v-row>
                        </template>
                      </v-img>
                    </div>
                    <div
                      v-else
                      class="thumbnail-preview-empty"
                    >
                      <v-icon
                        color="#404040"
                        size="48"
                        class="mb-2"
                      >
                        mdi-image-off-outline
                      </v-icon>
                      <div class="text-caption grey--text">
                        {{ $t('shop.admin_video_edit.thumbnail_no_image') }}
                      </div>
                    </div>

                  </v-card>
                </v-col>
              </v-row>
              <v-divider class="my-6"></v-divider>

              <!-- Community Section -->
              <div class="mb-2">
                <div class="text-body-2 font-weight-bold mb-3">{{ $t('shop.admin_video_edit.community_label') }}</div>

                <v-card
                  outlined
                  class="pa-4"
                >
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="text-body-2 font-weight-bold mb-1">
                        {{ $t('shop.admin_video_edit.comments_toggle') }}
                        <v-icon
                          v-if="get('video').comment"
                          color="success"
                          small
                          class="ml-2"
                        >mdi-check-circle</v-icon>
                      </div>
                      <div class="text-caption grey--text">{{ $t('shop.admin_video_edit.comments_hint') }}</div>
                    </div>
                    <v-switch
                      v-model="get('video').comment"
                      name="comment"
                      color="primary"
                      hide-details
                      class="mt-0"
                    ></v-switch>
                  </div>
                </v-card>
              </div>

            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Tab 2: Monetarisierung -->
      <v-tab-item>
        <v-card flat>
          <v-card-text class="px-0">
            <!-- Zugangsmodell -->
            <div class="mb-6">
              <div class="text-body-2 font-weight-bold mb-3">{{ $t('shop.admin_video_edit.access_model') }}</div>

              <v-card
                outlined
                class="pa-4 mb-4"
              >
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-body-2 font-weight-bold mb-1">{{ $t('shop.admin_video_edit.in_subscription') }}</div>
                    <div class="text-caption grey--text">{{ $t('shop.admin_video_edit.in_subscription_hint') }}</div>
                  </div>
                  <v-switch
                    v-model="get('video').subscription"
                    name="subscription"
                    color="primary"
                    hide-details
                    class="mt-0"
                  ></v-switch>
                </div>
              </v-card>

              <!-- Abo-Pläne (wenn Abo aktiv) -->
              <div v-if="get('video').subscription">
                <div class="mb-3">
                  <div class="text-body-2 font-weight-bold mb-2">{{ $t('shop.admin_video_edit.available_plans') }}</div>
                  <v-select
                    multiple
                    v-model="selectedPlans"
                    :items="get('plans')"
                    item-text="name"
                    item-value="id"
                    :placeholder="$t('shop.admin_video_edit.plans_placeholder')"
                    :rules="[(v) => (v && v.length > 0) || $t('shop.admin_video_edit.plans_required')]"
                    outlined
                    dense
                    chips
                    hide-details="auto"
                  >
                    <template v-slot:selection="{ item }">
                      <v-chip
                        color="primary"
                        small
                        close
                        @click:close="deletePlanFromVideo(item)"
                      >
                        {{ item.name }}
                      </v-chip>
                    </template>
                  </v-select>
                  <div class="text-caption grey--text mt-1">{{ $t('shop.admin_video_edit.plans_hint') }}</div>
                </div>

                <v-alert
                  type="info"
                  text
                  dense
                  icon="mdi-information-outline"
                >
                  {{ $t('shop.admin_video_edit.plans_info') }}
                </v-alert>
              </div>

              <!-- Einzelpreis (wenn nicht im Abo) -->
              <div v-else>
                <div class="text-body-2 font-weight-bold mb-2">{{ $t('shop.admin_video_edit.single_price_label') }}</div>
                <v-text-field
                  v-model="get('video').price"
                  type="text"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  name="price"
                  :placeholder="$t('shop.admin_video_edit.price_placeholder')"
                  prefix="€"
                  :rules="priceRules"
                  outlined
                  dense
                  required
                  hide-details="auto"
                ></v-text-field>
                <div class="text-caption grey--text mt-1">{{ $t('shop.admin_video_edit.price_free_or_min') }}</div>
              </div>
            </div>

          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Tab 3: Veröffentlichung -->
      <v-tab-item>
        <v-card flat>
          <v-card-text class="px-0">
            <!-- Sichtbarkeit Section -->
            <div class="mb-6">
              <div class="text-body-2 font-weight-bold mb-3">{{ $t('shop.admin_video_edit.visibility_label') }}</div>

              <v-card
                outlined
                class="pa-4"
              >
                <!-- On Stage -->
                <div class="d-flex align-center justify-space-between pb-4">
                  <div>
                    <div class="text-body-2 font-weight-bold mb-1">
                      {{ $t('shop.admin_video_edit.on_stage') }}
                      <v-icon
                        v-if="get('video').published"
                        color="success"
                        small
                        class="ml-2"
                      >mdi-check-circle</v-icon>
                    </div>
                    <div class="text-caption grey--text">{{ $t('shop.admin_video_edit.on_stage_hint') }}</div>
                  </div>
                  <v-switch
                    v-model="get('video').published"
                    name="published"
                    color="primary"
                    hide-details
                    class="mt-0"
                  ></v-switch>
                </div>

                <v-divider></v-divider>

                <!-- Veröffentlichen -->
                <div class="d-flex align-center justify-space-between pt-4">
                  <div>
                    <div class="text-body-2 font-weight-bold mb-1">
                      {{ $t('shop.admin_video_edit.publish') }}
                      <v-icon
                        v-if="get('video').share"
                        color="success"
                        small
                        class="ml-2"
                      >mdi-check-circle</v-icon>
                    </div>
                    <div class="text-caption grey--text">{{ $t('shop.admin_video_edit.publish_hint') }}</div>
                  </div>
                  <v-switch
                    v-model="get('video').share"
                    name="share"
                    color="primary"
                    hide-details
                    class="mt-0"
                  ></v-switch>
                </div>
              </v-card>

            </div>

            <!-- Share Links (nur wenn veröffentlicht) -->
            <div
              v-if="get('video').share"
              class="mb-6"
            >
              <v-card outlined>
                <v-card-text>
                  <div class="mb-4">
                    <div class="text-body-2 font-weight-bold mb-2">{{ $t('shop.admin_video_edit.embed_label') }}</div>
                    <div class="d-flex align-center">
                      <code class="flex-grow-1 pa-2 lighten-4 rounded text-caption">{{ shareItem(get("video")) }}</code>
                      <v-btn
                        @click="copyCodeSnippet(shareItem(get('video')))"
                        small
                        color="primary"
                        class="ml-2"
                        outlined
                      >
                        <v-icon
                          size="16"
                          class="mr-1"
                        >mdi-content-copy</v-icon>
                        {{ $t('shop.admin_video_edit.copy') }}
                      </v-btn>
                    </div>
                  </div>

                  <v-divider class="my-4"></v-divider>

                  <div>
                    <div class="text-body-2 font-weight-bold mb-2">{{ $t('shop.admin_video_edit.share_link_label') }}</div>
                    <div class="d-flex align-center">
                      <div class="flex-grow-1 text-caption">{{ shareLink(get("video")) }}</div>
                      <v-btn
                        @click="copyCodeSnippet(shareLink(get('video')))"
                        small
                        color="primary"
                        class="ml-2"
                        outlined
                      >
                        <v-icon
                          size="16"
                          class="mr-1"
                        >mdi-content-copy</v-icon>
                        {{ $t('shop.admin_video_edit.copy') }}
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>

          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

  </base-modal>
</template>
<script>
import adminVideoBearbeiten from "@/components/modal/adminVideoBearbeiten";
import APIService from "@/services/api.service";
import AppService from "@/services/app.service";
import { mapGetters } from "vuex";

export default {
  name: "adminVideoBearbeiten",
  extends: adminVideoBearbeiten,

  data() {
    return {
      activeTab: 0,
      saveLoader: false,
      valid: false,
      selectedCategories: [],
      initalCategories: [],
      isDraggingImage: false,
      selectedPlans: [],
      initalPlans: [],
      image: [],
      fskItems: [
        { id: 0, fsk: this.$t('shop.admin_video_edit.fsk_none') },
        { id: 16, fsk: this.$t('shop.admin_video_edit.fsk_16') },
        { id: 18, fsk: this.$t('shop.admin_video_edit.fsk_18') },
      ],
      videoTitleRules: [
        (v) => !!v || this.$t('shop.admin_video_edit.validation.title_required'),
        (v) => (v && v.length <= 52) || this.$t('shop.admin_video_edit.validation.title_max_length'),
      ],
      videoDescriptionRules: [
        (v) => !v || v.length <= 2000 || this.$t('shop.admin_video_edit.validation.description_max_length'),
      ],
      priceRules: [
        (v) => v !== null && v !== undefined && v !== '' || this.$t('shop.admin_video_edit.validation.price_required'),
        (v) => /^\d+([.,]\d{1,2})?$/.test(v) || this.$t('shop.admin_video_edit.validation.price_invalid'),
        (v) => {
          const price = parseFloat(String(v).replace(",", "."));
          return price === 0 || price >= 3 || this.$t('shop.admin_video_edit.validation.price_min');
        },
      ],
    };
  },

  computed: {
    ...mapGetters(["get"]),

    canSave() {
      // Mindestens eine Kategorie erforderlich
      if (!this.selectedCategories || this.selectedCategories.length === 0) {
        return false;
      }

      // Wenn Abo aktiv, mindestens ein Plan erforderlich
      if (this.get('video').subscription && (!this.selectedPlans || this.selectedPlans.length === 0)) {
        return false;
      }

      return true;
    },
  },

  watch: {
    "$store.state.main.video.result.id": {
      handler() {
        const newVal = this.$store.state.main.video;
        if (newVal && newVal.result) {
          if (newVal.result.categories && newVal.result.categories.length > 0) {
            this.selectedCategories = [
              ...newVal.result.categories.map((cat) => cat.id),
            ];
            this.initialCategories = [
              ...newVal.result.categories.map((cat) => cat.id),
            ];
          } else {
            this.selectedCategories = [];
            this.initialCategories = [];
          }
          if (newVal.result.plans && newVal.result.plans.length > 0) {
            this.selectedPlans = [
              ...newVal.result.plans.map((plan) => plan.id),
            ];
            this.initialPlans = [...newVal.result.plans.map((plan) => plan.id)];
          } else {
            this.selectedPlans = [];
            this.initialPlans = [];
          }
        }
      },
      immediate: true,
    },
  },

  methods: {
    imageSrc(path, width, height) {
      return AppService.imageSource(path, width, height);
    },

    thumbnailUpload(id) {
      setTimeout(() => {
        this.$store
          .dispatch("uploadFiles", {
            endpoint: `/api/v1/videos/${id}/upload`,
            files: { image: this.image },
            metadata: { width: 100, height: 100 },
            message: false,
          })
          .then((response) => {
            // Nur das Vorschaubild erneuern, die Formulareingaben bleiben stehen
            this.$set(this.get("video"), "image", response.result.image);
            APIService.setResponse({
              message: this.$t('shop.admin_video_edit.response.saved'),
              color: "blue",
            });
          })
          .catch((error) => {
            APIService.setResponse({
              message: this.$t('shop.admin_video_edit.response.upload_failed'),
              color: "red",
            });
          });
      }, 100);
    },

    shareItem(item, style = 'height="500px" width="100%') {
      const src = `${window.location.protocol}//${window.location.host}/videos/${item.id}`;
      let iframe = "";
      if (item.share) {
        iframe = `<iframe ${style}" src="${src}" title="${this.$store.state.main.config.result.site_name}"></iframe>`;
      }
      return iframe;
    },

    shareLink(item) {
      return `${window.location.protocol}//${window.location.host}/videos/${item.id}`;
    },

    copyCodeSnippet(item) {
      navigator.clipboard.writeText(item);
      APIService.setResponse({ message: this.$t('shop.admin_video_edit.response.copied'), color: "blue" });
    },

    close(name) {
      this.$store.dispatch("modal/close", name);
    },

    resetForm() {
      this.image = [];
    },

    deleteVideoFromCategory(item) {
      const index = this.selectedCategories.indexOf(item.id);
      if (index > -1) {
        this.selectedCategories.splice(index, 1);
      }
    },

    deletePlanFromVideo(item) {
      const index = this.selectedPlans.indexOf(item.id);
      if (index > -1) {
        this.selectedPlans.splice(index, 1);
      }
    },

    async saveVideoChange(e, id) {
      this.saveLoader = true;

      // Validierung: Mindestens eine Kategorie erforderlich
      if (!this.selectedCategories || this.selectedCategories.length === 0) {
        APIService.setResponse({
          message: this.$t('shop.admin_video_edit.response.category_required'),
          color: "red",
        });
        this.saveLoader = false;
        return;
      }

      // Validierung: Wenn Abo aktiv, mindestens ein Plan erforderlich
      if (this.get('video').subscription && (!this.selectedPlans || this.selectedPlans.length === 0)) {
        APIService.setResponse({
          message: this.$t('shop.admin_video_edit.response.plan_required'),
          color: "red",
        });
        this.saveLoader = false;
        return;
      }

      let object = APIService.getFormData(APIService.getForm(e));
      object = AppService.formatPlans(object, "video");

      if (object.subscription == true) {
        object.price = 0;
      } else {
        // Nur Preis verarbeiten wenn er nicht null/undefined ist
        if (object.price !== null && object.price !== undefined && object.price !== '') {
          object.price = parseFloat(String(object.price).replace(",", "."));
          if (object.price > 0 && object.price < 3) {
            APIService.setResponse({
              message: this.$t('shop.admin_video_edit.response.price_min_error'),
              color: "red",
            });
            this.saveLoader = false;
            return;
          }
        } else {
          // Preis nicht senden wenn er null ist und sich nicht geändert hat
          delete object.price;
        }
      }

      object.fsk = parseInt(object.fsk);

      const addedCategories = this.selectedCategories.filter(
        (catId) => !this.initialCategories.includes(catId)
      );
      const removedCategories = this.initialCategories.filter(
        (catId) => !this.selectedCategories.includes(catId)
      );
      const addedPlans = this.selectedPlans.filter(
        (planId) => !this.initialPlans.includes(planId)
      );
      const removedPlans = this.initialPlans.filter(
        (planId) => !this.selectedPlans.includes(planId)
      );

      // The API adds these plans to the plans of the video. The subscription
      // switch and the plans must arrive in ONE request: the API answers 422
      // when a request sets the subscription and carries no plan.
      if (addedPlans.length > 0) {
        object.plans = addedPlans;
      }

      try {
        await this.$store.dispatch("patch", {
          endpoint: `/api/v1/videos/${id}`,
          event: object,
          state: "video",
          message: false,
        });

        for (const categoryId of addedCategories) {
          await this.$store.dispatch("patch", {
            endpoint: `/api/v1/categories/${categoryId}`,
            event: { videos: [id] },
            state: "categories",
            message: false,
          });
        }

        for (const categoryId of removedCategories) {
          await this.$store.dispatch("delete", {
            endpoint: `/api/v1/categories/${categoryId}/videos/${id}`,
            state: "categories",
            message: false,
          });
        }

        for (const planId of removedPlans) {
          await this.$store.dispatch("delete", {
            endpoint: `/api/v1/videos/${id}/plans/${planId}`,
            state: "video",
            message: false,
          });
        }

        await this.$store.dispatch("fetch", {
          endpoint: "/api/v1/videos?sort=DESC",
          state: "videos",
        });

        this.initialCategories = [...this.selectedCategories];
        this.initialPlans = [...this.selectedPlans];

        this.$store.dispatch("modal/close", "adminVideoBearbeiten");
        this.resetForm();

        APIService.setResponse({
          message: this.$t('shop.admin_video_edit.response.save_success'),
          color: "green",
        });
        this.saveLoader = false;
      } catch (error) {
        const { response: { status }, } = error;

        APIService.setResponse({
          message: this.$t('shop.admin_video_edit.response.save_error', { status }),
          color: "red",
        });
        this.saveLoader = false;
      }
    },

    triggerImageInput() {
      if (this.$refs.image && this.$refs.image.$el) {
        const input = this.$refs.image.$el.querySelector('input[type="file"]');
        if (input) input.click();
      }
    },

    onDropImage(e) {
      this.isDraggingImage = false;
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        this.image = files;
      }
    },

    onBeforeDeleteImage: function (fileRecord) {
      if (confirm(this.$t('shop.admin_video_edit.response.confirm_delete_image'))) {
        this.$refs.image.deleteFileRecord(fileRecord);
      }
    },
  },

  mounted() {
    this.$store.dispatch("fetch", {
      endpoint: "/api/v1/plans",
      state: "plans",
    });
  },
};
</script>

<style scoped>
.gradient-avatar {
  background: linear-gradient(135deg, #00C3FF 0%, #0099CC 100%) !important;
}

.thumbnail-overlay {
  transition: opacity 0.2s;
}

.v-image:hover .thumbnail-overlay {
  opacity: 1 !important;
}

/* Tab styling */
::v-deep .v-tabs {
  border-radius: 10px !important;
}

::v-deep .v-tab {
  text-transform: none !important;
}

/* Drop Zone */
.drop-zone {
  border: 2px dashed #404040;
  border-radius: 8px;
  background: #0f0f0f;
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drop-zone:hover,
.drop-zone--active {
  border-color: #4a9eff;
  background: #151515;
}

.drop-zone-content {
  text-align: center;
  pointer-events: none;
}

.drop-zone-text {
  font-size: 16px;
  color: #b0b0b0;
  font-weight: 500;
}

.drop-zone-formats {
  font-size: 14px;
  color: #707070;
}

/* Thumbnail Preview */
.thumbnail-preview {
  background: #0f0f0f;
  border: 2px solid #404040;
  border-radius: 8px;
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumbnail-preview-wrapper {
  width: 100%;
  padding: 16px;
}

.thumbnail-preview-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

/* VueFileAgent Override */
.thumbnail-upload ::v-deep .file-preview-wrapper {
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  background: transparent !important;
}

.thumbnail-upload ::v-deep .file-preview-new {
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
  background: transparent !important;
}

.thumbnail-upload ::v-deep .file-preview {
  padding: 0 !important;
  background: transparent !important;
}
</style>